import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Counts web-font loads: text measured in a fallback font is the wrong size, so a fit taken before Livvic and
 * Fira Sans arrive must be taken again.
 */
function useFontsVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    const fonts = document.fonts;
    let active = true;
    const bump = () => {
      if (active) setVersion(v => v + 1);
    };
    fonts.addEventListener('loadingdone', bump);
    void fonts.ready.then(bump);
    return () => {
      active = false;
      fonts.removeEventListener('loadingdone', bump);
    };
  }, []);
  return version;
}

/**
 * Steps a layout through denser levels (0 = the frame's values) until its box stops overflowing, for the big
 * screen's worst cases (10 options of 80 characters, 10 long results). `resetKey` names what the content
 * and the box are: a change (or a web font arriving) starts again at level 0. Measures layout boxes
 * (`scrollHeight` against `clientHeight`), which the stage's scale transform does not change; the box's
 * children must not shrink (`shrink-0`), or a squashed child hides the overflow.
 */
export function useFitLevel(
  ref: RefObject<HTMLElement | null>,
  maxLevel: number,
  resetKey: string,
): number {
  const key = `${resetKey}|fonts:${useFontsVersion()}`;
  const [fit, setFit] = useState({ key, level: 0 });
  if (fit.key !== key) setFit({ key, level: 0 });
  const level = fit.key === key ? fit.level : 0;

  // No dependency list on purpose: it measures after every render and steps once per overflowing render,
  // stopping at a level that fits or at maxLevel, so it cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measure after every render (see above)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || level >= maxLevel) return;
    const overflows = el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
    if (overflows) setFit(f => (f.key === key ? { key, level: f.level + 1 } : f));
  });

  return level;
}
